import {
  AutocompleteFreeSoloValueMapping,
  AutocompleteHighlightChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  FilterOptionsState,
  inputBaseClasses,
  PopperProps,
  styled,
} from '@mui/material';
import React, { ForwardedRef, forwardRef } from 'react';
import { InputSize } from '../../model';
import { INPUT_BASE_PADDING_Y_PX } from '../../theme/component-overrides/input-base';
import { SxProps } from '../../utils';
import { VirtualizedAutocomplete } from '../../utils/VirtualizedAutocomplete';
import { MenuItem } from '../Menu';
import { TextField, TextFieldProps } from '../TextField';

export interface SelectOption extends Record<string, unknown> {
  /**
   * Key must be unique.
   */
  key: string;
  /**
   * Label of the option displayed in the dropdown menu.
   */
  label: string;
  /**
   * Optional description displayed below the label in the dropdown menu.
   */
  description?: string | React.ReactNode;
  /**
   * Disable the option from being selected
   */
  disabled?: boolean;
}

// Defaults to using SelectOption as type for options, but also allows using
// a type that extends from it for more complex use cases.
export interface SelectProps<
  Option extends SelectOption = SelectOption,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
> extends Pick<TextFieldProps, 'inputProps'>,
    Pick<AutocompleteProps<Option, false, DisableClearable, FreeSolo>, 'blurOnSelect'> {
  /**
   * The option elements to populate the select with.
   */
  options: Option[];
  /**
   * Render the option.
   *
   * This prop is prefixed with "dangerously" because in the the vast majority of product use cases,
   * this prop should NOT be used and you should rely on the default option handling for consistency.
   *
   * This prop is exposed primarily to enable some customization in other platform-level components.
   */
  dangerouslyRenderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Option,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  /**
   * If true, the input can't be cleared.
   */
  disableClearable?: DisableClearable;
  /**
   * If true, the component is disabled.
   */
  disabled?: boolean;
  /**
   * If `true`, the `input` will indicate an error.
   * The prop defaults to the value (`false`) inherited from the parent FormControl component.
   */
  error?: boolean;
  /**
   * A function that determines the filtered options to be rendered on search.
   *
   * @param {Option[]} options The options to render.
   * @param {object} state The state of the component.
   * @returns {Option[]}
   */
  filterOptions?: (options: Option[], state: FilterOptionsState<Option>) => Option[];
  /**
   * If true, the Autocomplete is free solo, meaning that the user input is not bound to provided options.
   * @default false
   */
  freeSolo?: FreeSolo;
  /**
   * Used to determine the string value for a given option. It's used to fill the input.
   */
  getOptionLabel?: (option: Option | AutocompleteFreeSoloValueMapping<FreeSolo>) => string;
  /**
   * The helper text content applied to the input element.
   */
  helperText?: React.ReactNode;
  /**
   * The id of the component.
   */
  id?: string;
  /**
   * Props applied to the Input element.
   *
   * If you pass in an object, it will be merged with the params from the Autocomplete, which may
   * lead to overriding of values (e.g. setting `endAdornment` will override the dropdown array).
   * Use the function if you need more customized handling of the InputProps.
   */
  InputProps?:
    | TextFieldProps['InputProps']
    | ((params: AutocompleteRenderInputParams['InputProps']) => TextFieldProps['InputProps']);
  /**
   * The input value.
   */
  inputValue?: string;
  /**
   * Used to determine if the option represents the given value.
   * Uses strict equality by default.
   * ⚠️ Both arguments need to be handled, an option can only match with one value.
   */
  isOptionEqualToValue?: (option: Option, value: Option) => boolean;
  /**
   * Label of the input
   */
  Label?: React.ReactNode;
  /**
   * Height for rendering the Listbox of options. Any CSS string value for the `height` property is valid (e.g. `100px`,
   * `3em`, etc.)
   */
  listboxHeight?: string;
  /**
   * Max height for rendering the Listbox of options. Any CSS string value for the `height` property is valid (e.g. `100px`,
   * `3em`). This will override the default max height of 40vh.
   */
  listboxMaxHeight?: string;
  /**
   * If true, the component is in a loading state.
   */
  loading?: boolean;
  /**
   * Text to display when there are no options.
   */
  noOptionsText?: React.ReactNode;
  /**
   * Callback fired when the component is blurred.
   */
  onBlur?: (e: React.SyntheticEvent) => void;
  /**
   * Callback fired when an option is selected.
   */
  onChange?: (e: React.SyntheticEvent, values: Option | AutocompleteFreeSoloValueMapping<FreeSolo> | null) => void;
  /**
   * Callback fired when the component requests to be closed.
   * Use in controlled mode (see open).
   *
   * @param {object} event The event source of the callback.
   */
  onClose?: (event: React.SyntheticEvent) => void;
  /**
   * Callback fired when the highlight option changes.
   */
  onHighlightChange?: (
    event: React.SyntheticEvent,
    option: Option | null,
    reason: AutocompleteHighlightChangeReason
  ) => void;
  /**
   * Callback fired when the input value changes.
   */
  onInputChange?: (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => void;
  /**
   * Callback fired when the component requests to be opened.
   * Use in controlled mode (see open).
   *
   * @param {object} event The event source of the callback.
   */
  onOpen?: (event: React.SyntheticEvent) => void;
  /**
   * If `true`, the component is shown.
   */
  open?: boolean;
  /**
   * Placement for the popup menu.
   * @default 'bottom-start'
   */
  popperPlacement?: PopperProps['placement'];
  /**
   * Width for the popup menu. When not set, the popup will match the width of the input.
   *
   * Useful in cases where the select is made very small to conserve space, but you do not want
   * to unnecessarily truncate the width of the menu items when opened.
   */
  popperWidth?: string;
  /**
   * The size of the component.
   * @default medium
   */
  size?: InputSize;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
  /**
   * The value to populate the selected options for the component.
   * If value is not passed in or passed as undefined, the component will stay in an uncontrolled state.
   * If value are passed in, use in controlled mode with the onChange prop.
   * Use value = null for no selection in controlled mode.
   * https://mui.com/material-ui/react-autocomplete/#controlled-states
   */
  value?: DisableClearable extends true ? Option : Option | null;
}

export const SELECT_DEFAULT_RENDER_OPTION: NonNullable<SelectProps['dangerouslyRenderOption']> = (props, option) => {
  return (
    <MenuItem
      {...props}
      key={option.key}
      description={option.description}
      disabled={option.disabled}
      sx={{ textWrap: 'wrap', wordBreak: 'break-all' }}
    >
      {option.label}
    </MenuItem>
  );
};

function _Select<Option extends SelectOption, DisableClearable extends boolean, FreeSolo extends boolean = false>(
  {
    options,
    Label,
    open,
    loading,
    disabled,
    error,
    helperText,
    size,
    inputProps,
    InputProps,
    filterOptions,
    noOptionsText,
    popperPlacement = 'bottom-start',
    popperWidth,
    dangerouslyRenderOption = SELECT_DEFAULT_RENDER_OPTION,
    ...props
  }: SelectProps<Option, DisableClearable, FreeSolo>,
  ref: ForwardedRef<HTMLDivElement>
) {
  return (
    <VirtualizedAutocomplete<Option, false, DisableClearable, FreeSolo>
      ref={ref}
      autoHighlight
      blurOnSelect
      multiple={false}
      {...props}
      disabled={disabled}
      open={open}
      loading={loading}
      options={options}
      noOptionsText={noOptionsText}
      filterOptions={filterOptions}
      renderOption={dangerouslyRenderOption}
      componentsProps={{
        popper: popperWidth
          ? {
              placement: popperPlacement,
              style: {
                width: popperWidth,
              },
            }
          : undefined,
      }}
      renderInput={(params) => {
        return (
          <SelectTextField
            {...params}
            ref={params.InputProps.ref}
            inputProps={{ ...params.inputProps, ...inputProps }}
            InputProps={
              typeof InputProps === 'function' ? InputProps(params.InputProps) : { ...params.InputProps, ...InputProps }
            }
            label={Label}
            error={error}
            helperText={helperText}
            size={size}
          />
        );
      }}
    />
  );
}

/**
 * @deprecated This component is deprecated. Please use the ComboboxSelect component instead.
 */
export const Select = forwardRef(_Select) as <
  Option extends SelectOption,
  DisableClearable extends boolean,
  FreeSolo extends boolean = false,
>(
  props: SelectProps<Option, DisableClearable, FreeSolo> & { ref?: ForwardedRef<HTMLDivElement> }
) => ReturnType<typeof _Select>;

const SelectTextField = styled(TextField)(() => {
  return {
    // Overriding a padding override for input base with a start adornment that is necesssary
    // for other use cases, but causes inconsistent sizing of the component for the `Select`.
    // Multiple classes and !important are needed here to sufficiently override the CSS used for
    // the original override.
    [`.${inputBaseClasses.root}.${inputBaseClasses.adornedStart}`]: {
      paddingTop: `${INPUT_BASE_PADDING_Y_PX.medium}px !important`,
      paddingBottom: `${INPUT_BASE_PADDING_Y_PX.medium}px !important`,

      [`&.${inputBaseClasses.sizeSmall}`]: {
        paddingTop: `${INPUT_BASE_PADDING_Y_PX.small}px !important`,
        paddingBottom: `${INPUT_BASE_PADDING_Y_PX.small}px !important`,
      },
    },
  };
});
